import { useFilter } from '@/zustand/Zustand';
import { ArrowRight, Check, Crown, Sparkles, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface UpgradeToPremiumButtonProps {
  variant?: 'default' | 'compact' | 'banner';
  showIcon?: boolean;
  className?: string;
}

interface FeatureComparison {
  upgradedFeatures: Array<{ basic: string; premium: string }>;
  newFeatures: string[];
  sharedFeatures: string[];
}

const getFeatureComparison = (basicFeatures: string[], premiumFeatures: string[]): FeatureComparison => {
  const basicSet = new Set<string>(basicFeatures);
  const premiumSet = new Set<string>(premiumFeatures);

  const upgradedFeatures: Array<{ basic: string; premium: string }> = [];
  const newFeatures: string[] = [];
  const sharedFeatures: string[] = [];

  const checkUpgrade = (basicFeature: string, premiumFeature: string) => {
    const basicWords = basicFeature.toLowerCase().split(' ');
    const premiumWords = premiumFeature.toLowerCase().split(' ');
    const sharedKeywords = basicWords.filter(word =>
      premiumWords.includes(word) && word.length > 3
    );
    return sharedKeywords.length >= 2;
  };

  premiumSet.forEach(premiumFeature => {
    if (basicSet.has(premiumFeature)) {
      sharedFeatures.push(premiumFeature);
    } else {
      let foundUpgrade = false;
      basicSet.forEach(basicFeature => {
        if (checkUpgrade(basicFeature, premiumFeature)) {
          upgradedFeatures.push({ basic: basicFeature, premium: premiumFeature });
          basicSet.delete(basicFeature);
          foundUpgrade = true;
        }
      });
      if (!foundUpgrade) {
        newFeatures.push(premiumFeature);
      }
    }
  });

  return { upgradedFeatures, newFeatures, sharedFeatures };
};

interface TooltipOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onBuy: () => void;
  comparison: FeatureComparison;
}

const TooltipOverlay: React.FC<TooltipOverlayProps> = ({ isOpen, onClose, onBuy, comparison }) => {
  const { t } = useTranslation('common');

  if (!isOpen) return null;

  const { upgradedFeatures, newFeatures, sharedFeatures } = comparison;

  const tooltipContent = (
    <div
      className="fixed inset-0 z-9999 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative bg-fp-bg border border-fp-border rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-linear-to-r from-red-600 to-orange-600 p-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white/20">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                  {t('tooltipOverlay.upgradeTitle')}
                  <Sparkles className="w-5 h-5 animate-pulse" />
                </h3>
                <p className="text-red-100 text-sm">{t('tooltipOverlay.upgradeSubtitle')}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors p-1"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {upgradedFeatures.length > 0 && (
            <div>
              <h4 className="text-green-400 font-semibold mb-3 flex items-center gap-2">
                <ArrowRight className="w-5 h-5" />
                {t('tooltipOverlay.enhancedFeatures')}
              </h4>
              <div className="space-y-3">
                {upgradedFeatures.map((feature, index) => (
                  <div key={index} className="bg-fp-card/50 border border-fp-border rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="shrink-0 mt-1">
                        <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center">
                          <X className="w-4 h-4 text-red-400" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-400 line-through text-sm">{feature.basic}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 mt-2">
                      <div className="shrink-0 mt-1">
                        <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                          <Check className="w-4 h-4 text-green-400" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium">{feature.premium}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {newFeatures.length > 0 && (
            <div>
              <h4 className="text-green-400 font-semibold mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                {t('tooltipOverlay.exclusiveFeatures')}
              </h4>
              <div className="space-y-2">
                {newFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3 bg-fp-card/30 border border-green-600/20 rounded-lg p-3">
                    <div className="shrink-0 mt-0.5">
                      <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-green-400" />
                      </div>
                    </div>
                    <p className="text-gray-200">{feature}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sharedFeatures.length > 0 && (
            <div>
              <h4 className="text-gray-400 font-semibold mb-3 text-sm">{t('tooltipOverlay.alsoIncluded')}</h4>
              <div className="grid grid-cols-1 gap-2">
                {sharedFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-gray-400 text-sm">
                    <Check className="w-4 h-4 text-gray-500 shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={onBuy}
            className="w-full bg-linear-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-white font-semibold py-4 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-red-500/50 flex items-center justify-center gap-2"
          >
            <Crown className="w-5 h-5" />
            <span>{t('buttons.upgradeNow')}</span>
            <Sparkles className="w-4 h-4 animate-pulse" />
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(tooltipContent, document.body);
};

export const UpgradeToPremiumButton: React.FC<UpgradeToPremiumButtonProps> = ({
  variant = 'default',
  showIcon = true,
  className = '',
}) => {
  const { t } = useTranslation(['common', 'subscription']);
  const { setIsFilterPanelOpen } = useFilter();
  const navigate = useNavigate();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const basicFeatures = useMemo(() => [
    t('subscription:features.basic.0'),
    t('subscription:features.basic.1'),
    t('subscription:features.basic.2'),
    t('subscription:features.basic.3'),
    t('subscription:features.basic.4'),
    t('subscription:features.basic.5'),
    t('subscription:features.basic.6'),
    t('subscription:features.basic.7'),
    t('subscription:features.basic.8'),
  ], [t]);

  const premiumFeatures = useMemo(() => [
    t('subscription:features.premium.0'),
    t('subscription:features.premium.1'),
    t('subscription:features.premium.2'),
    t('subscription:features.premium.3'),
    t('subscription:features.premium.4'),
    t('subscription:features.premium.5'),
    t('subscription:features.premium.6'),
    t('subscription:features.premium.7'),
    t('subscription:features.premium.8'),
    t('subscription:features.premium.9'),
    t('subscription:features.premium.10'),
    t('subscription:features.premium.11'),
  ], [t]);

  const comparison = useMemo(() => getFeatureComparison(basicFeatures, premiumFeatures), [basicFeatures, premiumFeatures]);

  const handleClick = () => setIsTooltipOpen(true);
  const handleClose = () => setIsTooltipOpen(false);
  const handleBuy = () => {
    setIsFilterPanelOpen(false);
    navigate('/subscription');
  };

  const tooltip = (
    <TooltipOverlay
      isOpen={isTooltipOpen}
      onClose={handleClose}
      onBuy={handleBuy}
      comparison={comparison}
    />
  );

  if (variant === 'default') {
    return (
      <>
        <button
          onClick={handleClick}
          className={`relative group overflow-hidden bg-linear-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-white text-sm font-semibold px-4 py-2 mr-5 rounded-md transition-all hover:shadow-lg hover:shadow-red-500/50 inline-flex items-center gap-2 ${className}`}
        >
          <span>{t('common:buttons.upgradeToPremium')}</span>
          <Sparkles className="w-4 h-4 animate-pulse shrink-0" />
          <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-linear-to-r from-transparent via-white/20 to-transparent" />
        </button>
        {tooltip}
      </>
    );
  }

  if (variant === 'compact') {
    return (
      <>
        <button
          onClick={handleClick}
          className={`inline-flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-500 text-white text-sm font-medium px-4 py-2 rounded-md transition-all hover:shadow-md ${className}`}
        >
          {showIcon && <Crown className="w-4 h-4" />}
          <span>{t('common:buttons.upgrade')}</span>
        </button>
        {tooltip}
      </>
    );
  }

  if (variant === 'banner') {
    return (
      <>
        <button
          onClick={handleClick}
          className={`relative w-full group overflow-hidden bg-linear-to-r from-gray-800 via-gray-900 to-gray-800 border border-red-600/30 hover:border-red-500/50 rounded-lg p-4 transition-all ${className}`}
        >
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-600/20 border border-red-600/30">
                <Crown className="w-5 h-5 text-red-400" />
              </div>
              <div className="text-left">
                <div className="text-white font-semibold flex items-center gap-2">
                  {t('common:buttons.unlockPremiumFeatures')}
                  <Sparkles className="w-4 h-4 text-red-400 animate-pulse" />
                </div>
                <div className="text-gray-400 text-sm">{t('common:buttons.getMoreResultsAndFilters')}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 rounded-md transition-colors duration-200">
              <span className="text-white font-medium">{t('common:buttons.upgradeNow')}</span>
              <svg className="w-4 h-4 text-white group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-linear-to-r from-red-600/5 via-red-500/10 to-red-600/5" />
        </button>
        {tooltip}
      </>
    );
  }

  return null;
};

export default UpgradeToPremiumButton;
